import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'

import * as API from '@/services/api.js'
import { handleError } from '@/services/errorHandler'

// Caches the v2 creative-intelligence reads (DNA + insights + ideas + coherence)
// per creativeId for the dashboard session, and POLLS while a creative's
// analysis is in progress (status: 'analyzing') so the tabs flip to live data
// the moment the-core finishes + materializes. Mounted in the dashboard stack.

const POLL_INTERVAL_MS = 4000
const POLL_MAX_MS = 5 * 60 * 1000   // give up after 5 min of 'analyzing'

const CreativeIntelligenceContext = createContext(null)

export function CreativeIntelligenceProvider({ children }) {

    const [insights, setInsights] = useState({})
    const [coherence, setCoherence] = useState({})

    // Ref mirrors (stable reads for dedupe) + poll bookkeeping.
    const refs = useRef({ insight: {}, coherence: {} })
    const setters = { insight: setInsights, coherence: setCoherence }
    const apis = {
        insight: API.generateCreativeInsightV2,
        // coherence: API.getCreativeCoherenceV2,
    }
    const timers = useRef({})       // `${kind}:${id}` → timeout id
    const deadlines = useRef({})    // `${kind}:${id}` → give-up timestamp

    // User-triggered (re)generation in-flight, tracked HERE (not in the tab) so the
    // "Generating…" state survives a tab switch / modal close — the work is owned by
    // the context. Ref = synchronous dup-guard; state = UI mirror.
    const generatingRef = useRef({})            // `${kind}:${id}` → true
    const [generating, setGenerating] = useState({})

    const store = (kind, id, data) => {
        refs.current[kind][id] = data
        setters[kind](prev => ({ ...prev, [id]: data }))
    }

    // Fetch once, store, and (re)arm polling while the creative is 'analyzing'.
    const refetch = useCallback(async (kind, creativeId, { regenerate = false } = {}) => {
        const key = `${kind}:${creativeId}`
        // Some kinds are intentionally disabled (e.g. coherence — no API wired up).
        // Bail before touching apis[kind] so a stray call can't throw "not a function".
        if (!apis[kind]) {
            return
        }
        // User-triggered (re)generation → flag it (survives tab/modal unmount) and
        // ignore a duplicate trigger while one is already running for this creative.
        if (regenerate) {
            if (generatingRef.current[key]) {
                return
            }
            generatingRef.current = { ...generatingRef.current, [key]: true }
            setGenerating({ ...generatingRef.current })
        }
        try {
            const { data } = await apis[kind]({ creativeId, regenerate })
            store(kind, creativeId, data)

            clearTimeout(timers.current[key])
            if (data?.status === 'analyzing') {
                if (!deadlines.current[key]) {
                    deadlines.current[key] = Date.now() + POLL_MAX_MS
                }
                if (Date.now() <= deadlines.current[key]) {
                    timers.current[key] = setTimeout(() => refetch(kind, creativeId), POLL_INTERVAL_MS)
                } else {
                    delete deadlines.current[key]   // gave up — leave last 'analyzing' state
                }
            } else {
                delete deadlines.current[key]       // ready/error/etc → stop polling
            }
            return data
        } catch (e) {
            handleError(e)
            const err = { status: 'error' }
            store(kind, creativeId, err)
            return err
        } finally {
            if (regenerate) {
                const next = { ...generatingRef.current }
                delete next[key]
                generatingRef.current = next
                setGenerating(next)
            }
        }
        // apis/setters/refs are stable; safe with empty deps.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // Cache-first load. Serve cache ONLY for a terminal-success result ('ok').
    // Any non-ok state (analyzing / not-grounded / analysis-unavailable / error)
    // is re-fetched so the tab picks up freshly-materialized DNA once the-core
    // finishes — otherwise a stale 'not-grounded' would stick forever.
    const load = useCallback((kind, creativeId) => {
        if (!creativeId) {
            return
        }
        const key = `${kind}:${creativeId}`
        const cached = refs.current[kind][creativeId]
        if (cached && cached.status === 'ok') {
            return
        }
        if (timers.current[key]) {
            return   // already polling
        }
        refetch(kind, creativeId)
    }, [refetch])

    // Clear all poll timers on unmount.
    useEffect(() => {
        const t = timers.current
        return () => {
            Object.values(t).forEach(clearTimeout)
        }
    }, [])

    const loadInsight = useCallback((id) => load('insight', id), [load])
    const loadCoherence = useCallback((id) => load('coherence', id), [load])
    const regenerateInsight = useCallback((id) => refetch('insight', id, { regenerate: true }), [refetch])
    // True while a regenerate is in-flight for this creative+kind — read by the tabs.
    const isGenerating = useCallback((kind, id) => !!generating[`${kind}:${id}`], [generating])

    // On-demand analysis trigger — POST to the CREATIVE service (never the-core),
    // then refetch so the tabs flip to 'analyzing' and the existing poll takes over.
    const triggerAnalysis = useCallback(async (creativeId) => {
        if (!creativeId) {
            return
        }
        // Optimistic: flip the tabs to 'analyzing' the instant the user clicks, so
        // the click always gives feedback even if the server resolves fast (the
        // route marks analyzing + enqueues right after). refetch then takes over
        // and the existing poll drives it to ready/failed.
        store('insight', creativeId, { status: 'analyzing' })
        try {
            await API.triggerCreativeAnalysis({ creativeId })
            refetch('insight', creativeId)
        } catch (e) {
            handleError(e)
            // Couldn't even kick it off — surface an error instead of spinning forever.
            store('insight', creativeId, { status: 'error' })
        }
    }, [refetch])

    // Bulk status for a set of creativeIds (lightweight, one-shot).
    const [statuses, setStatuses] = useState({})
    const loadStatuses = useCallback(async (creativeIds) => {
        const ids = (creativeIds ?? []).filter(Boolean)
        if (ids.length === 0) {
            return {}
        }
        try {
            const { data } = await API.getCreativeDnaStatuses({ creativeIds: ids })
            const map = data?.statuses ?? {}
            setStatuses(prev => ({ ...prev, ...map }))
            return map
        } catch (e) {
            handleError(e)
            return {}
        }
    }, [])

    // ── v1-logic insight/idea CARDS (CreativeView Intelligence + Ideas tabs) ──
    // The AI insights + ideas now come from the v1 brain logic (brand context +
    // descriptors + ads_performance), generated on-demand when the user visits the
    // tab. NO polling — plain request/response. DNA tags + analysis status still
    // come from the v2 `insight` kind above. These v1 endpoints need the adAccount
    // + date window (unlike the v2 ones), passed in via opts.
    const [insightCards, setInsightCards] = useState({})
    const [ideaCards, setIdeaCards] = useState({})
    const cardRefs = useRef({ insightCards: {}, ideaCards: {} })
    const cardSetters = { insightCards: setInsightCards, ideaCards: setIdeaCards }
    const cardApis = { insightCards: API.generateCreativeInsightCards, ideaCards: API.generateCreativeIdeaCards }
    const cardInflight = useRef({})
    const cardTimers = useRef({})     // `${kind}:${id}` → poll timeout (while 'not-ready')
    const cardDeadlines = useRef({})  // `${kind}:${id}` → give-up timestamp
    const cardGeneratingRef = useRef({})
    const [cardGenerating, setCardGenerating] = useState({})

    const storeCard = (kind, id, data) => {
        cardRefs.current[kind][id] = data
        cardSetters[kind](prev => ({ ...prev, [id]: data }))
    }

    // Fetch a v1 card once (dedup concurrent calls). regenerate bypasses the server cache.
    const fetchCard = useCallback(async (kind, creativeId, { adAccount, startDate, endDate, regenerate = false, poll = true }) => {
        const key = `${kind}:${creativeId}`
        if (regenerate) {
            // User retry / regenerate: dedup on the GENERATING flag, not the in-flight
            // load — so it always fires even if a background load/poll is mid-flight for
            // this key. It supersedes any active poll.
            if (cardGeneratingRef.current[key]) {
                return
            }
            cardGeneratingRef.current = { ...cardGeneratingRef.current, [key]: true }
            setCardGenerating({ ...cardGeneratingRef.current })
            clearTimeout(cardTimers.current[key])
        } else {
            // Background load / poll: dedup concurrent calls for this key.
            if (cardInflight.current[key]) {
                return
            }
            cardInflight.current[key] = true
        }
        try {
            const { data } = await cardApis[kind]({ creativeId, adAccount, startDate, endDate, regenerate })
            storeCard(kind, creativeId, data)
            // 'not-ready' = the creative's v1 analysis (descriptors / performance) is
            // still in progress — NOT a failure. Poll until ready (bounded), but ONLY
            // when the view is open (poll=true). The pre-warm fires poll=false → fire
            // and forget; the next tab open re-fetches and starts polling.
            clearTimeout(cardTimers.current[key])
            if (poll && data?.status === 'not-ready') {
                if (!cardDeadlines.current[key]) {
                    cardDeadlines.current[key] = Date.now() + POLL_MAX_MS
                }
                if (Date.now() <= cardDeadlines.current[key]) {
                    cardTimers.current[key] = setTimeout(() => fetchCard(kind, creativeId, { adAccount, startDate, endDate }), POLL_INTERVAL_MS)
                } else {
                    delete cardDeadlines.current[key]
                }
            } else {
                delete cardDeadlines.current[key]
            }
            return data
        } catch (e) {
            handleError(e)
            const err = { status: 'error' }
            storeCard(kind, creativeId, err)
            return err
        } finally {
            if (regenerate) {
                const next = { ...cardGeneratingRef.current }
                delete next[key]
                cardGeneratingRef.current = next
                setCardGenerating(next)
            } else {
                delete cardInflight.current[key]
            }
        }
        // apis/setters/refs are stable; safe with empty deps.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // Cache-first auto-load on tab visit. Serve cache only for a terminal 'ok';
    // re-fetch any non-ok (e.g. 'not-ready') so a freshly-analyzed creative picks up.
    const loadCard = useCallback((kind, creativeId, opts = {}) => {
        if (!creativeId || !opts.adAccount) {
            return
        }
        const key = `${kind}:${creativeId}`
        // Don't race a user regenerate that's mid-flight — it bypasses the cache and
        // must win; a background (cached) load resolving after it would overwrite it.
        if (cardGeneratingRef.current[key]) {
            return
        }
        const cached = cardRefs.current[kind][creativeId]
        if (cached && cached.status === 'ok') {
            return
        }
        fetchCard(kind, creativeId, opts)
    }, [fetchCard])

    const loadInsightCards = useCallback((id, opts) => loadCard('insightCards', id, opts), [loadCard])
    const loadIdeaCards = useCallback((id, opts) => loadCard('ideaCards', id, opts), [loadCard])
    const regenerateInsightCards = useCallback((id, opts) => fetchCard('insightCards', id, { ...opts, regenerate: true }), [fetchCard])
    const regenerateIdeaCards = useCallback((id, opts) => fetchCard('ideaCards', id, { ...opts, regenerate: true }), [fetchCard])
    const isGeneratingCard = useCallback((kind, id) => !!cardGenerating[`${kind}:${id}`], [cardGenerating])

    // One-shot pre-warm of the v1 insight + idea cards for a set of creatives (e.g.
    // the top-by-spend on first load) so the tabs/overview are ready when opened.
    // Cache-first + dedup live in fetchCard; the backend returns cached results for
    // already-generated creatives. Concurrency-capped to stay gentle on the LLM.
    const prewarmCards = useCallback(async (creativeIds, opts = {}) => {
        if (!opts.adAccount) {
            return
        }
        const ids = (creativeIds ?? []).filter(Boolean)
        const CONCURRENCY = 3
        let i = 0
        const worker = async () => {
            while (i < ids.length) {
                const id = ids[i++]
                // Fire-and-forget: poll=false so a 'not-ready' doesn't spin up a
                // background poll. Polling only matters when the view is actually open.
                await Promise.all([
                    fetchCard('insightCards', id, { ...opts, poll: false }),
                    fetchCard('ideaCards', id, { ...opts, poll: false }),
                ])
            }
        }
        await Promise.all(Array.from({ length: Math.min(CONCURRENCY, ids.length) }, worker))
    }, [fetchCard])

    // Clear card poll timers on unmount.
    useEffect(() => {
        const t = cardTimers.current
        return () => {
            Object.values(t).forEach(clearTimeout)
        }
    }, [])

    // ── comments cache (Comments tab) ───────────────────────────────────────
    // Synchronous request/response — comments + their AI summary resolve in one
    // round trip, so unlike the v1 cards above there's NO polling loop / deadline.
    // Mirrors the card subsystem's shape (ref for sync cache-first/dedup reads +
    // state map for render) but simplified: nothing to poll, nothing to clean up.
    const [commentsData, setCommentsData] = useState({})
    const [commentsSummaryData, setCommentsSummaryData] = useState({})
    const commentsRefs = useRef({ comments: {}, summary: {} })
    const commentsSetters = { comments: setCommentsData, summary: setCommentsSummaryData }
    const commentsApis = {
        comments: ({ integrationId, adId }) => API.getCreativeComments({ integrationId, adId }),
        summary: ({ creativeId, integrationId, adId }) => API.getCommentsSummary({ creativeId, adId, integrationId }),
    }
    const commentsInflight = useRef({})
    const commentsGeneratingRef = useRef({})
    const [commentsGenerating, setCommentsGenerating] = useState({})

    const storeComments = (kind, id, data) => {
        commentsRefs.current[kind][id] = data
        commentsSetters[kind](prev => ({ ...prev, [id]: data }))
    }

    // Fetch a comments kind once (dedup concurrent calls). regenerate bypasses the
    // cache-first check in loadCommentsForKind and OVERWRITES the stored entry.
    const fetchComments = useCallback(async (kind, creativeId, { integrationId, adId, regenerate = false } = {}) => {
        const key = `${kind}:${creativeId}`
        if (regenerate) {
            // User retry / regenerate: dedup on the GENERATING flag, not the in-flight
            // load — so it always fires even if a background load is mid-flight for
            // this key. It supersedes any active load.
            if (commentsGeneratingRef.current[key]) {
                return
            }
            commentsGeneratingRef.current = { ...commentsGeneratingRef.current, [key]: true }
            setCommentsGenerating({ ...commentsGeneratingRef.current })
        } else {
            // Background load: dedup concurrent calls for this key.
            if (commentsInflight.current[key]) {
                return
            }
            commentsInflight.current[key] = true
        }
        try {
            const { data } = await commentsApis[kind]({ creativeId, integrationId, adId })
            // Neither endpoint returns a `status` field — wrap it ourselves so the
            // cache-first check below has a terminal-success sentinel to serve.
            storeComments(kind, creativeId, { status: 'ok', ...data })
            return data
        } catch (e) {
            handleError(e)
            storeComments(kind, creativeId, { status: 'error' })
        } finally {
            if (regenerate) {
                const next = { ...commentsGeneratingRef.current }
                delete next[key]
                commentsGeneratingRef.current = next
                setCommentsGenerating(next)
            } else {
                delete commentsInflight.current[key]
            }
        }
        // apis/setters/refs are stable; safe with empty deps.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // Cache-first load. Serve cache only for a terminal 'ok'; no-op without params.
    const loadCommentsForKind = useCallback((kind, creativeId, { integrationId, adId } = {}) => {
        if (!creativeId || !integrationId || !adId) {
            return
        }
        const key = `${kind}:${creativeId}`
        // Don't race a user regenerate that's mid-flight — it bypasses the cache and
        // must win; a background (cached) load resolving after it would overwrite it.
        if (commentsGeneratingRef.current[key]) {
            return
        }
        const cached = commentsRefs.current[kind][creativeId]
        if (cached && cached.status === 'ok') {
            return
        }
        fetchComments(kind, creativeId, { integrationId, adId })
    }, [fetchComments])

    const loadComments = useCallback((id, opts) => loadCommentsForKind('comments', id, opts), [loadCommentsForKind])
    const loadCommentsSummary = useCallback((id, opts) => loadCommentsForKind('summary', id, opts), [loadCommentsForKind])
    const regenerateComments = useCallback((id, opts) => fetchComments('comments', id, { ...opts, regenerate: true }), [fetchComments])
    const regenerateCommentsSummary = useCallback((id, opts) => fetchComments('summary', id, { ...opts, regenerate: true }), [fetchComments])
    const isCommentsGenerating = useCallback((id) => !!commentsGenerating[`comments:${id}`], [commentsGenerating])
    const isCommentsSummaryGenerating = useCallback((id) => !!commentsGenerating[`summary:${id}`], [commentsGenerating])

    // ── transcript cache (Transcript tab) ───────────────────────────────────
    // Synchronous request/response, mirroring the comments cache above — but
    // single-kind (just a transcript, no list+summary split), so NO `kind`
    // dimension: refs/inflight/generating are keyed directly by creativeId.
    const [transcriptData, setTranscriptData] = useState({})
    const transcriptRefs = useRef({})
    const transcriptInflight = useRef({})
    const transcriptGeneratingRef = useRef({})
    const [transcriptGenerating, setTranscriptGenerating] = useState({})

    const storeTranscript = (creativeId, data) => {
        transcriptRefs.current[creativeId] = data
        setTranscriptData(prev => ({ ...prev, [creativeId]: data }))
    }

    // Fetch the transcript once (dedup concurrent calls). regenerate bypasses the
    // cache-first check in loadTranscript and OVERWRITES the stored entry.
    const fetchTranscript = useCallback(async (creativeId, { regenerate = false } = {}) => {
        if (regenerate) {
            // User retry / regenerate: dedup on the GENERATING flag, not the in-flight
            // load — so it always fires even if a background load is mid-flight for
            // this creative. It supersedes any active load.
            if (transcriptGeneratingRef.current[creativeId]) {
                return
            }
        } else {
            // Background load: dedup concurrent calls for this creative.
            if (transcriptInflight.current[creativeId]) {
                return
            }
            transcriptInflight.current[creativeId] = true
        }
        // Either path is now committed to a fetch — flip the reactive flag so
        // isTranscriptGenerating reflects ANY in-progress transcript fetch (initial
        // load or regenerate), not just a regenerate. The Transcript tab is
        // button-first and has no other loading signal to key its spinner off.
        transcriptGeneratingRef.current = { ...transcriptGeneratingRef.current, [creativeId]: true }
        setTranscriptGenerating({ ...transcriptGeneratingRef.current })
        try {
            const { data } = await API.generateTranscript({ creativeId, regenerate })
            storeTranscript(creativeId, { status: 'ok', ...data })
            return data
        } catch (e) {
            handleError(e)
            storeTranscript(creativeId, { status: 'error' })
        } finally {
            const next = { ...transcriptGeneratingRef.current }
            delete next[creativeId]
            transcriptGeneratingRef.current = next
            setTranscriptGenerating(next)
            delete transcriptInflight.current[creativeId]
        }
        // apis/setters/refs are stable; safe with empty deps.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // Cache-first load. Serve cache only for a terminal 'ok'; no-op without a creativeId.
    const loadTranscript = useCallback((creativeId) => {
        if (!creativeId) {
            return
        }
        // Don't race a user regenerate that's mid-flight — it bypasses the cache and
        // must win; a background (cached) load resolving after it would overwrite it.
        if (transcriptGeneratingRef.current[creativeId]) {
            return
        }
        const cached = transcriptRefs.current[creativeId]
        if (cached && cached.status === 'ok') {
            return
        }
        fetchTranscript(creativeId, {})
    }, [fetchTranscript])

    const regenerateTranscript = useCallback((creativeId) => fetchTranscript(creativeId, { regenerate: true }), [fetchTranscript])
    const isTranscriptGenerating = useCallback((creativeId) => !!transcriptGenerating[creativeId], [transcriptGenerating])

    return (
        <CreativeIntelligenceContext.Provider value={{
            insights, coherence, statuses,
            loadInsight, loadCoherence,
            regenerateInsight, isGenerating,
            triggerAnalysis,
            loadStatuses,
            // v1-logic insight/idea cards (Intelligence + Ideas tabs)
            insightCards, ideaCards,
            loadInsightCards, loadIdeaCards,
            regenerateInsightCards, regenerateIdeaCards, isGeneratingCard,
            prewarmCards,
            // comments cache (Comments tab) — synchronous, no polling
            commentsData, commentsSummaryData,
            loadComments, loadCommentsSummary,
            regenerateComments, regenerateCommentsSummary,
            isCommentsGenerating, isCommentsSummaryGenerating,
            // transcript cache (Transcript tab) — synchronous, no polling
            transcriptData,
            loadTranscript, regenerateTranscript, isTranscriptGenerating,
        }}>
            {children}
        </CreativeIntelligenceContext.Provider>
    )
}

export const useCreativeIntelligence = () => {
    const context = useContext(CreativeIntelligenceContext)
    if (!context) {
        throw new Error('useCreativeIntelligence must be used inside CreativeIntelligenceProvider')
    }
    return context
}
